/*
 * GameCallEngine — разбор и сравнение звука манка с эталонным голосом
 * птицы или зверя (/game-call-trainer).
 *
 * Почему свой разбор, а не PitchDetect (pitch-detect.js). Тот — для ГОЛОСА:
 * поиск лагов упирается в 2,4 кГц, а свист рябчика стоит на 7–8 кГц, вой
 * волка — на 0,4–1,2 кГц, бугл вапити уходит к 2,5 кГц. Здесь тон ищется по
 * гармонической сумме спектра внутри полосы вида, а у шумных голосов (кряк,
 * карканье, чуфыканье, рёв) вместо тона берётся спектральный центроид.
 *
 * Как сравниваем (и почему так):
 *   1. Звуки (ноты) — по огибающей энергии в полосе вида с гистерезисом.
 *   2. Ноты эталона и попытки выравниваются DTW по последовательности нот
 *      (с пропусками и вставками): ритм — это длительности и паузы
 *      сопоставленных нот, лишние и недостающие звуки.
 *   3. Тон сравнивается ВНУТРИ сопоставленных нот по нормированному времени
 *      ноты: общий сдвиг (медиана разностей, в центах) отдельно от формы
 *      (разброс вокруг сдвига) — «выше на 2 полутона» и «тон не падает в
 *      конце» — разные подсказки.
 *   4. Тембр — форма среднего спектра в полосе (dB, без общего уровня) и
 *      шумность (спектральная плоскостность).
 *
 * Один код и в браузере, и в node-тесте (tests/js/game-call-trainer-*.test.mjs).
 */
(function (root) {
    'use strict';

    var HOP_S = 0.01;
    var N_BANDS = 24;
    var EPS = 1e-12;

    function nextPow2(n) { var p = 1; while (p < n) p <<= 1; return p; }

    function fft(re, im) {
        var n = re.length, i, j, k, len, half, ang, wr, wi, ur, ui, vr, vi, tr, ti, cr, ci;
        for (i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) { tr = re[i]; re[i] = re[j]; re[j] = tr; ti = im[i]; im[i] = im[j]; im[j] = ti; }
        }
        for (len = 2; len <= n; len <<= 1) {
            half = len >> 1;
            ang = -2 * Math.PI / len;
            wr = Math.cos(ang); wi = Math.sin(ang);
            for (i = 0; i < n; i += len) {
                cr = 1; ci = 0;
                for (k = 0; k < half; k++) {
                    ur = re[i + k]; ui = im[i + k];
                    vr = re[i + k + half] * cr - im[i + k + half] * ci;
                    vi = re[i + k + half] * ci + im[i + k + half] * cr;
                    re[i + k] = ur + vr; im[i + k] = ui + vi;
                    re[i + k + half] = ur - vr; im[i + k + half] = ui - vi;
                    tr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = tr;
                }
            }
        }
    }

    function percentile(arr, q) {
        var a = Array.prototype.slice.call(arr).filter(function (v) { return isFinite(v); });
        if (!a.length) return NaN;
        a.sort(function (x, y) { return x - y; });
        var pos = (a.length - 1) * q, lo = Math.floor(pos), hi = Math.ceil(pos);
        return a[lo] + (a[hi] - a[lo]) * (pos - lo);
    }

    function median(arr) { return percentile(arr, 0.5); }

    function cents(f) { return 1200 * Math.log2(f); }

    /* Серия кряков или трель звучат без тишины между звуками: эхо и фон
       держат огибающую над порогом, и порог склеивает пять звуков в один.
       Делим звук в долине огибающей, если она глубже splitDb относительно
       меньшей из соседних вершин, а части не короче 40 мс. */
    function splitAtValleys(notes, envDb, splitDb) {
        var out = [];
        notes.forEach(function (n) {
            var len = n.e - n.s;
            if (len < 10) { out.push(n); return; }
            var sm = new Float32Array(len);
            for (var q = 0; q < len; q++) {
                var a = envDb[n.s + Math.max(0, q - 1)], b = envDb[n.s + q], c = envDb[n.s + Math.min(len - 1, q + 1)];
                sm[q] = (a + b + c) / 3;
            }
            var cutsAt = [], lastCut = 0;
            for (q = 4; q < len - 4; q++) {
                if (!(sm[q] <= sm[q - 1] && sm[q] <= sm[q + 1])) continue;
                var lp = -Infinity, rp = -Infinity, r;
                for (r = lastCut; r < q; r++) if (sm[r] > lp) lp = sm[r];
                for (r = q + 1; r < len; r++) { if (sm[r] > rp) rp = sm[r]; if (r - q > 40) break; }
                if (Math.min(lp, rp) - sm[q] >= splitDb && q - lastCut >= 4) { cutsAt.push(q); lastCut = q; }
            }
            var s = n.s;
            cutsAt.forEach(function (cq) { out.push({ s: s, e: n.s + cq }); s = n.s + cq + 1; });
            out.push({ s: s, e: n.e });
        });
        return out;
    }

    /* Полоса вида задаёт всё: где искать звук, тон и тембр. Частоты за
       пределами полосы (гул машины, ветер, соседняя птица) в разбор не идут. */
    function analyze(x, sr, opts) {
        opts = opts || {};
        var nyq = sr / 2;
        var band = opts.band || [100, Math.min(12000, nyq * 0.95)];
        var lo = Math.max(40, band[0]);
        var hi = Math.min(band[1], nyq * 0.98);
        var cut = { lo: lo, hi: hi, limited: band[1] > nyq * 0.98 };
        var win = nextPow2(Math.round(sr * 0.032));
        var nfft = win * 2;
        var hop = Math.max(1, Math.round(sr * HOP_S));
        var nFrames = x.length >= win ? Math.floor((x.length - win) / hop) + 1 : 0;
        var binHz = sr / nfft;
        var kLo = Math.max(1, Math.floor(lo / binHz));
        var kHi = Math.min(nfft / 2 - 1, Math.ceil(hi / binHz));
        var w = new Float32Array(win);
        for (var i = 0; i < win; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (win - 1));

        // Края полос — логарифмически между lo и hi.
        var edges = new Float32Array(N_BANDS + 1);
        for (var b = 0; b <= N_BANDS; b++) edges[b] = lo * Math.pow(hi / lo, b / N_BANDS);
        var bandOfBin = new Int16Array(kHi + 1).fill(-1);
        for (var k = kLo; k <= kHi; k++) {
            var f = k * binHz, bi = 0;
            while (bi < N_BANDS - 1 && f >= edges[bi + 1]) bi++;
            bandOfBin[k] = bi;
        }

        // Кандидаты тона: четверть полутона в полосе.
        var pLo = Math.max(lo, opts.pitchLo || lo), pHi = Math.min(hi, opts.pitchHi || hi);
        var cands = [];
        for (var c = cents(pLo); c <= cents(pHi); c += 25) cands.push(Math.pow(2, c / 1200));

        // Вся полоса записи — чтобы отличить «звука нет» от «звук не там».
        var kF0 = Math.max(1, Math.floor(60 / binHz)), kF1 = Math.min(nfft / 2 - 1, Math.floor(nyq * 0.98 / binHz));
        var fullDb = new Float32Array(nFrames);
        var fullCen = new Float32Array(nFrames);
        var envDb = new Float32Array(nFrames);
        var flat = new Float32Array(nFrames);
        var f0 = new Float32Array(nFrames);
        var sal = new Float32Array(nFrames);
        var cen = new Float32Array(nFrames);
        var bands = new Float32Array(nFrames * N_BANDS);
        var re = new Float64Array(nfft), im = new Float64Array(nfft), mag = new Float64Array(nfft / 2);
        var peak = 0, clipN = 0;
        for (i = 0; i < x.length; i++) {
            var av = x[i] < 0 ? -x[i] : x[i];
            if (av > peak) peak = av;
            if (av >= 0.985) clipN++;
        }

        for (var fr = 0; fr < nFrames; fr++) {
            var i0 = fr * hop;
            for (i = 0; i < win; i++) { re[i] = x[i0 + i] * w[i]; im[i] = 0; }
            for (i = win; i < nfft; i++) { re[i] = 0; im[i] = 0; }
            fft(re, im);
            var pw = 0, logSum = 0, nb = 0, cnum = 0;
            var bp = new Float64Array(N_BANDS);
            for (k = kLo; k <= kHi; k++) {
                var p2 = (re[k] * re[k] + im[k] * im[k]) / (win * win);
                mag[k] = Math.sqrt(p2);
                pw += p2; logSum += Math.log(p2 + EPS); nb++;
                cnum += p2 * k * binHz;
                bp[bandOfBin[k]] += p2;
            }
            var fp = 0, fc2 = 0;
            for (k = kF0; k <= kF1; k++) {
                var q2 = (re[k] * re[k] + im[k] * im[k]) / (win * win);
                fp += q2; fc2 += q2 * k * binHz;
            }
            fullDb[fr] = 10 * Math.log10(fp + EPS);
            fullCen[fr] = fp > 0 ? fc2 / fp : 0;
            envDb[fr] = 10 * Math.log10(pw + EPS);
            flat[fr] = nb ? Math.exp(logSum / nb) / (pw / nb + EPS) : 1;
            cen[fr] = pw > 0 ? cnum / pw : 0;
            for (b = 0; b < N_BANDS; b++) bands[fr * N_BANDS + b] = 10 * Math.log10(bp[b] + EPS);

            // Гармоническая сумма: f0 и его кратные с убывающим весом.
            var best = 0, bestF = 0;
            for (var ci = 0; ci < cands.length; ci++) {
                var fc = cands[ci], s = 0, wk = 1;
                for (var h = 1; h <= 6; h++) {
                    var fh = fc * h;
                    if (fh > hi * 1.02 || fh >= nyq) break;
                    var kk = Math.round(fh / binHz);
                    var m = mag[kk];
                    if (kk > kLo && mag[kk - 1] > m) m = mag[kk - 1];
                    if (kk < kHi && mag[kk + 1] > m) m = mag[kk + 1];
                    s += wk * m;
                    wk *= 0.75;
                }
                if (s > best) { best = s; bestF = fc; }
            }
            // Уточнение по параболе вокруг пика основного тона.
            if (bestF > 0) {
                var k0 = Math.round(bestF / binHz), kb = k0;
                for (var dk = -2; dk <= 2; dk++) {
                    var kq = k0 + dk;
                    if (kq > kLo && kq < kHi && mag[kq] > mag[kb]) kb = kq;
                }
                if (kb > kLo && kb < kHi) {
                    var a1 = mag[kb - 1], a2 = mag[kb], a3 = mag[kb + 1], den = a1 - 2 * a2 + a3;
                    var off = den !== 0 ? 0.5 * (a1 - a3) / den : 0;
                    if (Math.abs(off) < 1) bestF = (kb + off) * binHz;
                }
            }
            f0[fr] = bestF;
            sal[fr] = best;
        }

        // Шумовой пол и порог активности с гистерезисом. Цифровая тишина
        // (дополненный файл, поддельный микрофон, ворклет до первого звука) —
        // не фон комнаты: с ней пол уходит в −120 дБ, порог — под шум между
        // звуками, и серия кряков склеивается в один звук (замер стендом).
        var audible = [];
        for (fr = 0; fr < nFrames; fr++) if (envDb[fr] > -110) audible.push(envDb[fr]);
        var noiseDb = audible.length >= 10 ? percentile(audible, 0.1) : percentile(envDb, 0.1);
        var peakDb = percentile(envDb, 0.995);
        var span = Math.max(0, peakDb - noiseDb);
        // Границы звука — от ВЕРШИНЫ записи, а не от фона: у эталона фон —
        // лес, у попытки — комната, и порог «от фона» резал одну и ту же трель
        // по-разному (стенд с фоном комнаты: ритм 59 у точной копии эталона).
        // Фон держит пол порога там, где запись тихая.
        var onTh = Math.max(noiseDb + 9, peakDb - 24);
        var offTh = Math.max(noiseDb + 6, peakDb - 30);
        // Попытку режем ТЕМ ЖЕ порогом от вершины, что и эталон (analyzeAttempt):
        // у короткого эталона без тишины «фон» — это паузы между кряками, и его
        // порог выше; иначе серия эталона делится на кряки, а та же серия в
        // тихой комнате — на сплошные звуки без пауз (стенд: ритм 30 у копии).
        if (opts.relOn != null) {
            onTh = Math.max(noiseDb + 6, peakDb + opts.relOn);
            offTh = Math.max(noiseDb + 4, peakDb + (opts.relOff != null ? opts.relOff : opts.relOn - 6));
        }
        var act = new Uint8Array(nFrames), on = false;
        for (fr = 0; fr < nFrames; fr++) {
            if (!on && envDb[fr] >= onTh) on = true;
            else if (on && envDb[fr] < offTh) on = false;
            act[fr] = on ? 1 : 0;
        }
        var minGap = opts.mergeGapFrames != null ? opts.mergeGapFrames : 3;
        var minNote = opts.minNoteFrames != null ? opts.minNoteFrames : 3;
        var notes = [];
        for (fr = 0; fr < nFrames;) {
            if (!act[fr]) { fr++; continue; }
            var st = fr;
            while (fr < nFrames && act[fr]) fr++;
            notes.push({ s: st, e: fr });
        }
        var merged = [];
        notes.forEach(function (n) {
            var last = merged[merged.length - 1];
            if (last && n.s - last.e <= minGap) last.e = n.e;
            else merged.push({ s: n.s, e: n.e });
        });
        // Глубина долины — своя у вида; у тихой записи долина не может быть
        // глубже её динамики, поэтому порог не выше 60 % от неё.
        var splitDb = Math.min(opts.splitDb || 6, Math.max(4, 0.6 * span));
        notes = splitAtValleys(merged, envDb, splitDb).filter(function (n) { return n.e - n.s >= minNote; });

        // Звонкость кадра: тон есть, если спектр не плоский и сумма уверенная.
        var voiceFlat = opts.voicedFlatness || 0.3;
        var voiced = new Uint8Array(nFrames);
        notes.forEach(function (n) {
            for (var q = n.s; q < n.e; q++) voiced[q] = flat[q] < voiceFlat && f0[q] > 0 ? 1 : 0;
        });

        // Средний спектр по звукам — форма тембра (без общего уровня).
        // Шумность — средняя по ЭНЕРГИИ: края звука тонут в фоне комнаты и не
        // должны делать свист «шипящим».
        var ltasPow = new Float64Array(N_BANDS), nAct = 0, flatSum = 0, flatW = 0;
        notes.forEach(function (n) {
            for (var q = n.s; q < n.e; q++) {
                for (var bb = 0; bb < N_BANDS; bb++) ltasPow[bb] += Math.pow(10, bands[q * N_BANDS + bb] / 10);
                var pq = Math.pow(10, envDb[q] / 10);
                flatSum += flat[q] * pq; flatW += pq; nAct++;
            }
        });
        var ltas = new Float32Array(N_BANDS), meanL = 0;
        for (b = 0; b < N_BANDS; b++) { ltas[b] = 10 * Math.log10(ltasPow[b] / Math.max(1, nAct) + EPS); meanL += ltas[b]; }
        meanL /= N_BANDS;
        for (b = 0; b < N_BANDS; b++) ltas[b] -= meanL;

        var activeS = notes.length ? notes[0].s : 0;
        var activeE = notes.length ? notes[notes.length - 1].e : 0;
        var noteDb = notes.length ? percentile(envDb.subarray(activeS, activeE), 0.9) : noiseDb;

        // Доля энергии звуков в полосе вида: свист рябчика, просвистанный на
        // 2 кГц, — не тот звук, сколько бы ни совпал ритм.
        var inPw = 0, allPw = 0;
        notes.forEach(function (n) {
            for (var q = n.s; q < n.e; q++) { inPw += Math.pow(10, envDb[q] / 10); allPw += Math.pow(10, fullDb[q] / 10); }
        });
        // Верх звука (90-й процентиль тона или центроида) — его обязана
        // вместить частота дискретизации попытки.
        var tops = [];
        notes.forEach(function (n) {
            for (var q = n.s; q < n.e; q++) tops.push(voiced[q] ? f0[q] : cen[q]);
        });
        var topHz = tops.length ? percentile(tops, 0.9) : 0;
        // Громкий звук где угодно в записи — и где он стоит по частоте.
        var fNoise = percentile(fullDb, 0.1), fPeak = percentile(fullDb, 0.995);
        var fTh = fNoise + Math.max(9, 0.32 * Math.max(0, fPeak - fNoise));
        var lcW = 0, lcS = 0, loudN = 0;
        for (fr = 0; fr < nFrames; fr++) {
            if (fullDb[fr] < fTh) continue;
            var wq = Math.pow(10, fullDb[fr] / 10);
            lcW += wq; lcS += wq * fullCen[fr]; loudN++;
        }

        return {
            sr: sr, hop: hop, hopS: hop / sr, frames: nFrames, band: [lo, hi], cut: cut,
            envDb: envDb, flat: flat, f0: f0, salience: sal, centroid: cen, bands: bands, nBands: N_BANDS,
            edges: edges, notes: notes, voiced: voiced, ltas: ltas,
            flatness: flatW > 0 ? flatSum / flatW : 1,
            bandShare: allPw > 0 ? inPw / allPw : 0, topHz: topHz,
            loudFrames: loudN, loudCentroid: lcW > 0 ? lcS / lcW : 0, fullSnrDb: fPeak - fNoise,
            noiseDb: noiseDb, peakDb: peakDb, snrDb: noteDb - noiseDb,
            relOn: onTh - peakDb, relOff: offTh - peakDb, splitBase: opts.splitDb || 6,
            peak: peak, clipped: clipN > Math.max(3, x.length * 0.002),
            activeStartS: activeS * hop / sr, activeEndS: activeE * hop / sr,
            durationS: (activeE - activeS) * hop / sr,
        };
    }

    /* Контур ноты по нормированному времени (0…1) — тон для тональных видов,
       центроид — для шумных. Кадры без тона пропускаются, контур
       интерполируется; null — если звонких кадров слишком мало. */
    function noteContour(A, n, kind, points) {
        var xs = [], ys = [];
        for (var q = n.s; q < n.e; q++) {
            var v = 0;
            if (kind === 'noisy') v = A.centroid[q];
            else if (A.voiced[q]) v = A.f0[q];
            if (v > 0) { xs.push((q - n.s + 0.5) / (n.e - n.s)); ys.push(cents(v)); }
        }
        var need = Math.max(2, Math.round((n.e - n.s) * 0.25));
        if (xs.length < need) return null;
        var out = new Float32Array(points), j = 0;
        for (var p = 0; p < points; p++) {
            var t = (p + 0.5) / points;
            while (j < xs.length - 2 && xs[j + 1] < t) j++;
            var x0 = xs[j], x1 = xs[Math.min(j + 1, xs.length - 1)];
            var y0 = ys[j], y1 = ys[Math.min(j + 1, ys.length - 1)];
            var u = x1 > x0 ? Math.min(1, Math.max(0, (t - x0) / (x1 - x0))) : 0;
            out[p] = y0 + (y1 - y0) * u;
        }
        // Мелкое дрожание кадров (±30 центов) — не форма: сглаживаем по 3.
        var sm = new Float32Array(points);
        for (p = 0; p < points; p++) {
            var a = out[Math.max(0, p - 1)], b2 = out[p], c2 = out[Math.min(points - 1, p + 1)];
            sm[p] = (a + b2 + c2) / 3;
        }
        return sm;
    }

    /* DTW по нотам: длительность (лог) и пауза перед нотой; пропуск и
       вставка ноты стоят штраф. Возвращает пары [i, j] и счёт пропусков. */
    function alignNotes(R, T) {
        var rn = R.notes, tn = T.notes, nR = rn.length, nT = tn.length;
        var GAP = 1.1;
        function dur(A, n) { return (n.e - n.s) * A.hopS; }
        function gapBefore(A, list, i) { return i > 0 ? (list[i].s - list[i - 1].e) * A.hopS : 0; }
        function cost(i, j) {
            var dl = Math.abs(Math.log(Math.max(0.02, dur(T, tn[j])) / Math.max(0.02, dur(R, rn[i]))));
            var dg = Math.abs(gapBefore(T, tn, j) - gapBefore(R, rn, i));
            return dl * 1.2 + Math.min(1.5, dg / 0.25);
        }
        var D = [], P = [];
        for (var i = 0; i <= nR; i++) { D.push(new Float64Array(nT + 1).fill(Infinity)); P.push(new Int8Array(nT + 1)); }
        D[0][0] = 0;
        for (i = 0; i <= nR; i++) {
            for (var j = 0; j <= nT; j++) {
                if (i === 0 && j === 0) continue;
                var best = Infinity, bp = 0;
                if (i > 0 && j > 0 && D[i - 1][j - 1] + cost(i - 1, j - 1) < best) { best = D[i - 1][j - 1] + cost(i - 1, j - 1); bp = 1; }
                if (i > 0 && D[i - 1][j] + GAP < best) { best = D[i - 1][j] + GAP; bp = 2; }
                if (j > 0 && D[i][j - 1] + GAP < best) { best = D[i][j - 1] + GAP; bp = 3; }
                D[i][j] = best; P[i][j] = bp;
            }
        }
        var pairs = [], missing = 0, extra = 0;
        i = nR; j = nT;
        while (i > 0 || j > 0) {
            var m = P[i][j];
            if (m === 1) { pairs.push([i - 1, j - 1]); i--; j--; }
            else if (m === 2) { missing++; i--; }
            else { extra++; j--; }
        }
        pairs.reverse();
        return { pairs: pairs, missing: missing, extra: extra, total: D[nR][nT] };
    }

    function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

    var WEIGHTS = {
        whistle: { pitch: 0.45, rhythm: 0.35, timbre: 0.20 },
        harmonic: { pitch: 0.45, rhythm: 0.30, timbre: 0.25 },
        noisy: { pitch: 0.25, rhythm: 0.45, timbre: 0.30 },
    };

    /* Сравнение попытки с эталоном. kind — whistle | harmonic | noisy.
       hints — коды с числами; слова подставляет страница из перевода. */
    function compare(R, T, kind) {
        kind = WEIGHTS[kind] ? kind : 'harmonic';
        var out = { kind: kind, hints: [], ok: true };
        var bandLo = R.band[0], bandHi = R.band[1];
        // Гарнитура Bluetooth пишет 8–16 кГц: свист рябчика ей не виден, и
        // всё, что она «нашла» в полосе, — отражения и шум, а не звук.
        if (R.topHz > 0 && T.sr / 2 * 0.92 < R.topHz) {
            out.ok = false; out.score = 0; out.reason = 'band_cut';
            out.cut = { sr: T.sr, needHz: Math.round(R.topHz) };
            return out;
        }
        // Хвост попытки («ну как?», щелчок кнопки) — не часть зова: звуки,
        // начавшиеся позже двойной длины эталона от первого звука, не считаем.
        if (T.notes.length > 1) {
            var lim = T.notes[0].s + Math.round((R.durationS * 2.2 + 0.8) / T.hopS);
            var kept = T.notes.filter(function (n) { return n.s <= lim; });
            if (kept.length < T.notes.length) {
                T = Object.assign({}, T, { notes: kept, durationS: (kept[kept.length - 1].e - kept[0].s) * T.hopS });
                out.trimmedTail = true;
            }
        }
        if (!T.notes.length || T.snrDb < 8) {
            out.ok = false;
            out.score = 0;
            if (T.loudFrames >= 10 && T.fullSnrDb >= 15) {
                out.reason = 'off_band';
                out.offBand = { hz: T.loudCentroid, lo: bandLo, hi: bandHi, below: T.loudCentroid < bandLo };
            } else out.reason = T.peak < 0.02 ? 'too_quiet' : 'no_sound';
            return out;
        }
        // Большая часть звука — вне полосы вида: это другой звук. Когда в полосе
        // почти ничего (меньше 5 % доли эталона), «звуки» там — фон комнаты:
        // не оценка 3 из 100, а прямой ответ, где звук на самом деле.
        var shareRatio = R.bandShare > 0 ? T.bandShare / R.bandShare : 1;
        out.bandShare = shareRatio;
        if (shareRatio < 0.05 && T.loudFrames >= 10) {
            out.ok = false; out.score = 0; out.reason = 'off_band';
            out.offBand = { hz: T.loudCentroid, lo: bandLo, hi: bandHi, below: T.loudCentroid < bandLo };
            return out;
        }
        if (shareRatio < 0.5) {
            out.hints.push({ code: T.loudCentroid < bandLo ? 'mostly_below' : 'mostly_above', lo: bandLo, hi: bandHi, w: 1.05 });
        }
        var al = alignNotes(R, T);
        var nR = R.notes.length, nT = T.notes.length;

        // ── Ритм ──────────────────────────────────────────────────────────
        var durR = R.durationS, durT = T.durationS;
        var pairCost = 0, worst = null;
        al.pairs.forEach(function (pq, idx) {
            var rn = R.notes[pq[0]], tn = T.notes[pq[1]];
            var dr = (rn.e - rn.s) * R.hopS, dt = (tn.e - tn.s) * T.hopS;
            var c = Math.abs(Math.log(Math.max(0.02, dt) / Math.max(0.02, dr)));
            pairCost += c;
            if (!worst || Math.abs(dt - dr) > Math.abs(worst.diff)) worst = { ref: pq[0], diff: dt - dr, rel: c };
        });
        var nPairs = Math.max(1, al.pairs.length);
        var gapsR = [], gapsT = [];
        for (var gi = 1; gi < al.pairs.length; gi++) {
            var a0 = al.pairs[gi - 1], a1 = al.pairs[gi];
            if (a1[0] === a0[0] + 1 && a1[1] === a0[1] + 1) {
                gapsR.push((R.notes[a1[0]].s - R.notes[a0[0]].e) * R.hopS);
                gapsT.push((T.notes[a1[1]].s - T.notes[a0[1]].e) * T.hopS);
            }
        }
        var gapDiff = gapsR.length ? median(gapsT.map(function (g, k) { return g - gapsR[k]; })) : 0;
        var durRatio = durR > 0 ? durT / durR : 1;
        var rhythmCost = 1.1 * pairCost / nPairs
            + 0.55 * (al.missing + al.extra) / Math.max(nR, 1)
            + 0.9 * Math.abs(Math.log(Math.max(0.05, durRatio)))
            + Math.min(0.6, Math.abs(gapDiff) / 0.4);
        var rhythm = 100 * Math.exp(-1.15 * rhythmCost);
        out.rhythm = {
            score: Math.round(rhythm), refNotes: nR, tryNotes: nT, missing: al.missing, extra: al.extra,
            durationRatio: durRatio, gapDiffS: gapDiff, worst: worst, pairs: al.pairs,
        };
        if (nT < nR && al.missing) out.hints.push({ code: 'notes_fewer', ref: nR, got: nT, w: 0.9 });
        else if (nT > nR && al.extra) out.hints.push({ code: 'notes_more', ref: nR, got: nT, w: 0.8 });
        if (Math.abs(Math.log(durRatio)) > 0.22) {
            out.hints.push({ code: durRatio > 1 ? 'too_long' : 'too_short', pct: Math.round(Math.abs(durRatio - 1) * 100), w: 0.75 });
        }
        if (worst && worst.rel > 0.35 && Math.abs(worst.diff) >= 0.08 && nR > 1) {
            out.hints.push({ code: worst.diff > 0 ? 'note_longer' : 'note_shorter', n: worst.ref + 1, s: Math.abs(worst.diff), w: 0.7 });
        }
        if (Math.abs(gapDiff) >= 0.08) out.hints.push({ code: gapDiff > 0 ? 'gaps_longer' : 'gaps_shorter', s: Math.abs(gapDiff), w: 0.6 });

        // ── Тон (или центроид у шумных) ───────────────────────────────────
        var P = 24, diffs = [], perNote = [], slopeR = [], slopeT = [], weightSum = 0;
        al.pairs.forEach(function (pq) {
            var rn = R.notes[pq[0]], tn = T.notes[pq[1]];
            var cr = noteContour(R, rn, kind, P), ct = noteContour(T, tn, kind, P);
            if (!cr || !ct) return;
            var wNote = (rn.e - rn.s);
            var d = [];
            for (var p = 0; p < P; p++) d.push(ct[p] - cr[p]);
            perNote.push({ ref: pq[0], offset: median(d), w: wNote });
            for (p = 0; p < P; p++) diffs.push({ v: d[p], w: wNote });
            weightSum += wNote;
            var third = Math.max(1, Math.floor(P / 3));
            var hr = 0, tr = 0, ht = 0, tt = 0;
            for (p = 0; p < third; p++) { hr += cr[p]; ht += ct[p]; }
            for (p = P - third; p < P; p++) { tr += cr[p]; tt += ct[p]; }
            slopeR.push({ v: (tr - hr) / third, w: wNote });
            slopeT.push({ v: (tt - ht) / third, w: wNote });
        });
        if (diffs.length) {
            var vals = diffs.map(function (d) { return d.v; });
            var offset = median(vals);
            var spread = median(vals.map(function (v) { return Math.abs(v - offset); }));
            // Октавная путаница: у бугла и гогота в полосе две «основных» —
            // нижний гул и верхний свист. Разность, сложенная в одну октаву,
            // с заметно меньшим разбросом — это та же мелодия, разобранная на
            // октаву иначе, а не фальшь в 12 полутонов.
            if (kind === 'harmonic' && Math.abs(offset) > 700) {
                var folded = vals.map(function (v) { var r = ((v % 1200) + 1800) % 1200 - 600; return r; });
                var fOff = median(folded);
                var fSpread = median(folded.map(function (v) { return Math.abs(v - fOff); }));
                if (fSpread < spread * 0.7 || fSpread < 150) { offset = fOff; spread = fSpread; out.octaveFolded = true; }
            }
            var wavg = function (arr) { var s = 0, ww = 0; arr.forEach(function (a) { s += a.v * a.w; ww += a.w; }); return ww ? s / ww : 0; };
            var sR = wavg(slopeR), sT = wavg(slopeT);
            // У шумных голосов «высота» — центроид, а его сдвигает и микрофон:
            // шкала вдвое мягче тональной.
            var offScale = kind === 'noisy' ? 700 : 330;
            var shapeScale = kind === 'noisy' ? 320 : 190;
            var slopeErr = Math.abs(sT - sR);
            var pitch = 100 * Math.exp(-Math.pow(Math.abs(offset) / offScale, 1.6)) * Math.exp(-spread / shapeScale)
                * Math.exp(-Math.max(0, slopeErr - 120) / 900);
            out.pitch = {
                score: Math.round(pitch), offsetCents: offset, spreadCents: spread, slopeRef: sR, slopeTry: sT,
                perNote: perNote, measuredNotes: perNote.length,
            };
            if (Math.abs(offset) >= (kind === 'noisy' ? 350 : 120)) {
                out.hints.push({ code: kind === 'noisy' ? (offset > 0 ? 'brighter' : 'duller') : (offset > 0 ? 'pitch_high' : 'pitch_low'),
                    st: Math.abs(offset) / 100, w: 0.95 });
            }
            if (Math.abs(sR) >= 150 && Math.sign(sT) !== Math.sign(sR) || Math.abs(sR) >= 150 && Math.abs(sT) < Math.abs(sR) * 0.4) {
                out.hints.push({ code: sR < 0 ? 'should_fall' : 'should_rise', w: 0.8 });
            } else if (Math.abs(sR) < 100 && Math.abs(sT) >= 250) {
                out.hints.push({ code: 'keep_steady', w: 0.6 });
            }
            if (spread > shapeScale * 1.2) out.hints.push({ code: 'pitch_wobbly', w: 0.55 });
            if (perNote.length > 1) {
                var worstN = perNote.reduce(function (a, b) { return Math.abs(b.offset - offset) > Math.abs(a.offset - offset) ? b : a; });
                if (Math.abs(worstN.offset - offset) >= 200 && kind !== 'noisy') {
                    out.hints.push({ code: worstN.offset > offset ? 'note_high' : 'note_low', n: worstN.ref + 1, st: Math.abs(worstN.offset - offset) / 100, w: 0.65 });
                }
            }
        } else {
            out.pitch = { score: kind === 'noisy' ? 50 : 0, offsetCents: 0, spreadCents: 0, measuredNotes: 0 };
            if (kind !== 'noisy') out.hints.push({ code: 'no_tone', w: 0.9 });
        }

        // ── Тембр ─────────────────────────────────────────────────────────
        // У свиста спектр — это и есть тон: форма спектра повторила бы ось
        // тона второй раз. Тембр свиста — его чистота (шум, хрип, «пшик»).
        // У остальных — форма среднего спектра, сравниваемая со сдвигом до
        // ±3 полос: сдвиг — это «выше/ниже», о нём говорит ось тона.
        var flatRatio = (T.flatness + 0.02) / (R.flatness + 0.02);
        var noisePenalty = Math.exp(-Math.max(0, Math.abs(Math.log(flatRatio)) - 0.35) * 0.9);
        var half = R.nBands / 2, lowR = 0, lowT = 0, highR = 0, highT = 0;
        for (var b = 0; b < R.nBands; b++) {
            if (b < half) { lowR += R.ltas[b]; lowT += T.ltas[b]; } else { highR += R.ltas[b]; highT += T.ltas[b]; }
        }
        var tilt = ((highT - lowT) - (highR - lowR)) / half;
        var dist = 0, timbre;
        if (kind === 'whistle') {
            timbre = 100 * noisePenalty;
        } else {
            var bestD = Infinity;
            for (var sh = -3; sh <= 3; sh++) {
                var sq = 0, cnt = 0, mR = 0, mT = 0, bb2;
                for (bb2 = 0; bb2 < R.nBands; bb2++) { var tb = bb2 + sh; if (tb < 0 || tb >= T.nBands) continue; mR += R.ltas[bb2]; mT += T.ltas[tb]; cnt++; }
                if (cnt < R.nBands - 3) continue;
                mR /= cnt; mT /= cnt;
                for (bb2 = 0; bb2 < R.nBands; bb2++) { var tb2 = bb2 + sh; if (tb2 < 0 || tb2 >= T.nBands) continue; var dd = (T.ltas[tb2] - mT) - (R.ltas[bb2] - mR); sq += dd * dd; }
                var dsh = Math.sqrt(sq / cnt) + Math.abs(sh) * 0.6;
                if (dsh < bestD) bestD = dsh;
            }
            dist = bestD;
            timbre = 100 * Math.exp(-Math.pow(dist / 11, 2)) * noisePenalty;
        }
        out.timbre = { score: Math.round(timbre), distanceDb: dist, tiltDb: tilt, flatRatio: flatRatio };
        // У свиста наклон спектра в узкой полосе — это тот же тон: о нём уже сказала ось тона.
        if (kind !== 'whistle' && tilt <= -6) out.hints.push({ code: 'needs_highs', w: 0.5 });
        else if (kind !== 'whistle' && tilt >= 6) out.hints.push({ code: 'too_bright', w: 0.45 });
        if (flatRatio > 2.2 && R.flatness < 0.35) out.hints.push({ code: 'too_noisy', w: 0.7 });
        else if (flatRatio < 0.45 && R.flatness >= 0.35) out.hints.push({ code: 'too_clean', w: 0.5 });

        // ── Условия записи ─────────────────────────────────────────────────
        if (T.clipped) out.hints.push({ code: 'clipped', w: 1 });
        else if (T.snrDb < 15) out.hints.push({ code: 'quiet', w: 0.85 });

        var W = WEIGHTS[kind];
        var total = W.pitch * out.pitch.score + W.rhythm * out.rhythm.score + W.timbre * out.timbre.score;
        if (shareRatio < 0.5) total *= Math.max(0.25, shareRatio / 0.5);
        out.score = Math.round(clamp01(total / 100) * 100);
        out.hints.sort(function (a, b) { return b.w - a.w; });
        return out;
    }

    /* Свой эталон: полоса и род звука — по самой записи. Полоса — там, где
       лежат 3…97 % энергии звуков; род — по шумности. */
    function guessProfile(x, sr) {
        var wide = analyze(x, sr, { band: [80, Math.min(16000, sr / 2 * 0.95)] });
        if (!wide.notes.length) return null;
        var pw = [], tot = 0;
        for (var b = 0; b < wide.nBands; b++) { var v = Math.pow(10, wide.ltas[b] / 10); pw.push(v); tot += v; }
        var acc = 0, bLo = 0, bHi = wide.nBands - 1;
        for (b = 0; b < wide.nBands; b++) { acc += pw[b]; if (acc / tot >= 0.03) { bLo = b; break; } }
        acc = 0;
        for (b = wide.nBands - 1; b >= 0; b--) { acc += pw[b]; if (acc / tot >= 0.03) { bHi = b; break; } }
        var lo = wide.edges[bLo] * 0.85, hi = Math.min(sr / 2 * 0.95, wide.edges[bHi + 1] * 1.15);
        var narrow = analyze(x, sr, { band: [lo, hi] });
        var kind = narrow.flatness < 0.12 ? 'whistle' : narrow.flatness < 0.3 ? 'harmonic' : 'noisy';
        return { band: [Math.round(lo), Math.round(hi)], kind: kind };
    }

    /* Попытка разбирается по правилам своего эталона: та же полоса, та же
       глубина деления и тот же порог звука относительно вершины. */
    function analyzeAttempt(R, x, sr, opts) {
        opts = opts || {};
        return analyze(x, sr, Object.assign({ band: R.band, splitDb: R.splitBase, relOn: R.relOn, relOff: R.relOff }, opts));
    }

    var api = { analyze: analyze, analyzeAttempt: analyzeAttempt, compare: compare, guessProfile: guessProfile, alignNotes: alignNotes, _fft: fft, HOP_S: HOP_S };
    root.GameCallEngine = api;
})(typeof window !== 'undefined' ? window : globalThis);
